import { db } from '@/js/firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { logger } from './loggerService';
import { userService } from './userService';
import { CLIENT_PERMISSION_LEVELS } from '@/constants/clientPermissions';

/**
 * Agency settings live in `agencies/{agencyId}` where the doc ID IS the agency's
 * own company doc ID — agencies/X and companies/X are the same organization, so
 * there is no join. Name, branding, logo, storage and contacts stay on the company
 * doc and are reused as-is.
 *
 * markupPercent lives here rather than on the company doc on purpose: firestore.rules
 * grants client users a writable field allowlist on `companies`, and agency pricing
 * must stay off any doc a client can touch.
 */
class AgencyService {
  constructor() {
    this.collection = 'agencies';
    this.logger = logger;
  }

  async getAgency(agencyId) {
    if (!agencyId) return null;
    try {
      const snap = await getDoc(doc(db, this.collection, agencyId));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (error) {
      this.logger.error('[AgencyService] Failed to get agency', {
        error: error.message,
        agencyId,
      });
      throw error;
    }
  }

  /**
   * Create the settings doc for an agency. `agencyId` must be the ID of the
   * company record already created with isAgency: true.
   */
  async createAgency(agencyId, { markupPercent = 0, accountNotes = '' } = {}) {
    try {
      await setDoc(doc(db, this.collection, agencyId), {
        markupPercent: this._normalizeMarkup(markupPercent),
        active: true,
        // Maintained by syncAgencyUserAccess; lets an agency's CLIENT read this
        // doc to resolve markup when pricing an order.
        linkedCompanyIds: [],
        accountNotes,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      this.logger.info('[AgencyService] Agency created', { agencyId });
      return agencyId;
    } catch (error) {
      this.logger.error('[AgencyService] Failed to create agency', {
        error: error.message,
        agencyId,
      });
      throw error;
    }
  }

  async updateAgency(agencyId, updates) {
    try {
      const clean = { ...updates, updatedAt: serverTimestamp() };
      if ('markupPercent' in clean) {
        clean.markupPercent = this._normalizeMarkup(clean.markupPercent);
      }
      await updateDoc(doc(db, this.collection, agencyId), clean);
      this.logger.info('[AgencyService] Agency updated', { agencyId });
    } catch (error) {
      this.logger.error('[AgencyService] Failed to update agency', {
        error: error.message,
        agencyId,
      });
      throw error;
    }
  }

  /**
   * Companies on this agency's roster.
   *
   * The agency's OWN company is not matched by the query — its record carries
   * `agencyId: null` — so it is fetched separately when `includeOwn` is set. It
   * is an ordinary Vysual company and belongs in the list alongside the rest.
   *
   * @param {string} agencyId
   * @param {{ includeOwn?: boolean }} [options]
   */
  async getAgencyCompanies(agencyId, { includeOwn = false } = {}) {
    try {
      const [snap, own] = await Promise.all([
        getDocs(query(collection(db, 'companies'), where('agencyId', '==', agencyId))),
        includeOwn
          ? getDoc(doc(db, 'companies', agencyId)).catch(() => null)
          : Promise.resolve(null),
      ]);

      const linked = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (own?.exists()) {
        // Own company first — it is the agency's home account.
        return [{ id: own.id, ...own.data() }, ...linked];
      }
      return linked;
    } catch (error) {
      this.logger.error('[AgencyService] Failed to list agency companies', {
        error: error.message,
        agencyId,
      });
      throw error;
    }
  }

  /** Agency staff. Per-company access still lives in each user's companies[]. */
  async getAgencyUsers(agencyId) {
    try {
      const snap = await getDocs(
        query(collection(db, 'users'), where('agencyId', '==', agencyId)),
      );
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      this.logger.error('[AgencyService] Failed to list agency users', {
        error: error.message,
        agencyId,
      });
      throw error;
    }
  }

  /**
   * Everyone the agency can put on staff: members of the agency's own company,
   * plus anyone already flagged as its staff. A superset of getAgencyUsers,
   * which only returns people who already hold the role — you have to be able to
   * see someone before you can promote them.
   *
   * Delegates the membership scan to userService, which filters companies[]
   * client-side: the entries are objects with varying shapes, so Firestore
   * array queries can't match them reliably.
   */
  async getAgencyPeople(agencyId) {
    try {
      const [members, staff] = await Promise.all([
        userService.getCompanyUsers(agencyId),
        this.getAgencyUsers(agencyId),
      ]);

      const merged = new Map();
      for (const u of [...members, ...staff]) merged.set(u.id, u);
      return [...merged.values()];
    } catch (error) {
      this.logger.error('[AgencyService] Failed to list agency people', {
        error: error.message, agencyId,
      });
      throw error;
    }
  }

  /**
   * Agency users who should be auto-subscribed to a project on `companyId` —
   * those whose companies[] grants them that company.
   */
  async getAgencySubscribersFor(agencyId, companyId) {
    if (!agencyId || !companyId) return [];
    const users = await this.getAgencyUsers(agencyId);
    return users
      .filter((u) =>
        (u.companies || []).some((c) =>
          typeof c === 'string' ? c === companyId : c?.companyId === companyId,
        ),
      )
      .map((u) => u.id);
  }

  /**
   * Make a user agency staff, or revoke it.
   *
   * Enabling an agency does NOT promote that company's users — being a member of
   * the agency's company and being agency STAFF are separate things, so this is
   * always an explicit act.
   *
   * The agencyId claim follows via the existing syncCompanyIdsClaim trigger, but
   * only lands in the user's token on their next refresh — they must sign out and
   * back in before the agency portal will let them in.
   *
   * @param {string} userId
   * @param {string|null} agencyId - null to revoke
   * @param {'admin'|'member'} role
   */
  async setAgencyStaff(userId, agencyId, role = 'member') {
    try {
      const isAdmin = role === 'admin';
      const snap = await getDoc(doc(db, 'users', userId));
      if (!snap.exists()) throw new Error(`User ${userId} not found`);

      const current = snap.data().companies || [];
      // On revoke the caller passes agencyId null, so the roster to clean up has
      // to come from what the user is being removed FROM.
      const targetAgencyId = agencyId || snap.data().agencyId || null;
      const idOf = (c) => (typeof c === 'string' ? c : c?.companyId);

      const updates = {
        agencyId: agencyId || null,
        agencyRole: agencyId ? role : null,
        agencyAllCompanies: agencyId ? isAdmin : false,
      };

      // syncAgencyUserAccess only fires when a COMPANY is written, so it can't
      // backfill someone promoted after the roster already exists. Reconcile
      // companies[] here so an agency admin can see clients linked earlier.
      const roster = targetAgencyId
        ? await this.getAgencyCompanies(targetAgencyId)
        : [];

      if (roster.length) {
        if (agencyId && isAdmin) {
          const have = new Set(current.map(idOf));
          updates.companies = [
            ...current,
            ...roster
              .filter((c) => !have.has(c.id))
              .map((c) => ({
                companyId: c.id,
                companyName: c.name || '',
                role: 'user',
                // Full working access, but not the client's own company settings
                // or billing — the agency runs the work, not the account. Must
                // stay in step with AGENCY_PERMISSION_LEVEL in
                // functions/agency/agencyShared.js.
                permissionLevel: CLIENT_PERMISSION_LEVELS.AGENCY,
                joinedAt: new Date().toISOString(),
                isPrimary: false,
                status: 'active',
              })),
          ];
        } else if (!agencyId) {
          // Revoking: drop the agency's CLIENT companies. Membership of the
          // agency's own company is untouched — that's their employer.
          const rosterIds = roster.map((c) => c.id);
          updates.companies = current.filter((c) => !rosterIds.includes(idOf(c)));
        }
      }

      await updateDoc(doc(db, 'users', userId), updates);
      this.logger.info('[AgencyService] Agency staff updated', {
        userId, agencyId, role, rosterSize: roster.length,
      });
    } catch (error) {
      this.logger.error('[AgencyService] Failed to set agency staff', {
        error: error.message, userId, agencyId,
      });
      throw error;
    }
  }

  _normalizeMarkup(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.min(100, Math.max(0, n));
  }
}

export const agencyService = new AgencyService();
